import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ArrowRight } from "lucide-react";
import { fetchGoogleReviews } from "@/utils/googleReviews";
import { ReviewCTARead, ReviewCTAWrite } from "@/components/ReviewCTAs";
import GoogleReviewTiles from "@/components/GoogleReviewTiles";
import reviewsData from "@/data/reviews.json";

// Exported so the homepage credentials strip renders the same Google mark
// rather than carrying a second copy of the artwork.
export function GoogleG({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="flex-shrink-0">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function HeaderSummary({ rating, count }) {
  const display = rating ? Number(rating).toFixed(1) : "4.8";
  const filled = Math.round(Number(display));
  return (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-center sm:text-left">
      <div className="flex items-center gap-2">
        <GoogleG size={22} />
        <span className="font-heading font-bold text-brand-black text-base">Google Reviews</span>
      </div>
      <div className="hidden sm:block w-px h-8 bg-brand-light-grey" aria-hidden="true" />
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-0.5" aria-label={`${display} out of 5 stars`}>
          {[1, 2, 3, 4, 5].map((s) => (
            <Star
              key={s}
              size={18}
              className={s <= filled ? "fill-amber-400 text-amber-400" : "text-brand-light-grey"}
            />
          ))}
        </div>
        <span className="font-heading text-2xl font-bold text-brand-black">{display}</span>
        {count > 0 && <span className="text-sm text-brand-grey">{`· ${count} reviews`}</span>}
      </div>
    </div>
  );
}

/**
 * Full homepage Google Reviews block.
 * - Summary header (rating + count from shared fetch).
 * - Tiled reviews via reusable <GoogleReviewTiles />.
 * - Fallback copy when reviews unavailable.
 * - READ primary + WRITE subtle-link secondary CTA (separated intent).
 */
export default function GoogleReviewsBanner() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchGoogleReviews().then((d) => {
      if (!cancelled) setData(d);
    });
    return () => { cancelled = true; };
  }, []);

  const place = data?.place || null;
  const rating = place?.rating || null;
  const count = place?.reviewCount || 0;
  const hasReviews = Array.isArray(data?.reviews) && data.reviews.length > 0;

  return (
    <div className="max-w-5xl mx-auto" data-testid="google-reviews-banner">
      <div className="mb-6 flex flex-col items-center gap-4">
        <HeaderSummary rating={rating} count={count} />
      </div>

      {hasReviews ? (
        <GoogleReviewTiles limit={3} testId="google-reviews-list" />
      ) : (
        // Fallback — API unconfigured or returned no reviews.
        <div className="rounded-2xl border border-brand-light-grey bg-white px-6 py-8 text-center" data-testid="google-reviews-fallback">
          <p className="text-sm text-brand-grey max-w-md mx-auto">
            Our customers rate us on Google — read what UK businesses are saying about switching to CanDo Payments.
          </p>
        </div>
      )}

      <div className="mt-6 flex flex-col items-center gap-3" data-testid="google-reviews-ctas">
        <ReviewCTARead variant="primary" count={count} testId="google-reviews-read-primary" />
        <Link
          to="/reviews"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-aqua-dark hover:text-brand-black transition-colors group"
          data-testid="google-reviews-see-all-internal"
        >
          {`See all ${reviewsData.totals.total} verified reviews on our site`}
          <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
        </Link>
        <ReviewCTAWrite variant="link" testId="google-reviews-write-secondary" />
      </div>
    </div>
  );
}
